import {
  ExclamationCircleOutlined,
  MoreOutlined,
  PlusCircleOutlined,
} from '@ant-design/icons';
import { closestCenter, DndContext, DragEndEvent } from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { Button, Col, Dropdown, MenuProps, Row, Space, Typography } from 'antd';
import { memo, useCallback, useEffect } from 'react';
import { get, useFieldArray, useFormContext } from 'react-hook-form';
import styled from 'styled-components';

import { useProgramStepContext } from 'src/features/programsV2/contexts/ProgramStepContext';
import { useDropdownManager } from 'src/features/programsV2/hooks/useDropdownManager';
import { ContentStepForm } from 'src/features/programsV2/schemas/contentStepSchema';
import { createEmptyContentWithInstructor } from 'src/features/programsV2/utils/formHelpers';
import { showConfirmModal } from 'src/features/programsV2/utils/modalHelpers';

import { SortableItem } from '../../ui/SortableItem';
import { TransparentButton } from '../../ui/TransparentButton';
import { ContentCard } from './ContentCard';

interface ProgramContentsProps {
  moduleIndex: number;
}

const { Text } = Typography;

export const ProgramContents = memo<ProgramContentsProps>(({ moduleIndex }) => {
  const {
    control,
    setValue,
    getValues,
    formState: { errors },
  } = useFormContext<ContentStepForm>();
  const { programApiInitialValues } = useProgramStepContext();

  const { fields, append, remove, insert, move } = useFieldArray({
    control,
    name: `modules.${moduleIndex}.contents`,
  });

  const contentErrors: [] = get(errors, `modules.${moduleIndex}.contents`);

  const EMPTY_CONTENT_WITH_INSTRUCTOR = createEmptyContentWithInstructor(
    programApiInitialValues?.initialStep.instructorIds[0] ?? 0
  );

  const {
    dropdownStates,
    toggleDropdown,
    updateDropdownStates,
    updateDropdownsOrder,
    openAllDropdowns,
  } = useDropdownManager();

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      if (active.id !== over?.id) {
        const oldIndex = Number(active.id);
        const newIndex = Number(over?.id);

        move(oldIndex, newIndex);

        updateDropdownsOrder(oldIndex, newIndex);
      }
    },
    [getValues, updateDropdownsOrder, move]
  );

  const addEmptyContent = () => {
    append(EMPTY_CONTENT_WITH_INSTRUCTOR);
  };

  const handleDuplicate = (index: number) => {
    const contents = getValues(`modules.${moduleIndex}.contents`);
    if (!contents) return;
    const contentToDuplicate = contents[index];
    insert(index + 1, {
      ...contentToDuplicate,
      title: `${contentToDuplicate.title} (copy)`,
    });
  };

  const handleClear = (index: number) => {
    const contents = getValues(`modules.${moduleIndex}.contents`);
    if (!contents) return;
    const contentName = contents[index].title;
    const clearedContent = contents.map((content, i) =>
      i === index ? EMPTY_CONTENT_WITH_INSTRUCTOR : content
    );
    showConfirmModal({
      icon: <ExclamationCircleOutlined style={{ color: 'red' }} />,
      title: 'Limpar conteúdo',
      content: (
        <Space
          size={'small'}
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
          }}
        >
          <Row>
            <Text>
              {contentName
                ? `Deseja realmente limpar todos os campos de "${contentName}"? `
                : 'Deseja realmente limpar todos os campos deste conteúdo? '}
            </Text>
          </Row>
          <Row>
            <Text> Essa ação não poderá ser desfeita.</Text>
          </Row>
        </Space>
      ),
      cancelText: 'Cancelar',
      okText: 'Limpar Conteúdo',
      okButtonProps: {
        style: { backgroundColor: 'red', color: 'white', borderColor: 'red' },
      },
      okType: 'danger',
      onOk: () => {
        setValue(`modules.${moduleIndex}.contents`, clearedContent);
      },
    });
  };

  const handleRemove = (index: number) => {
    const contents = getValues(`modules.${moduleIndex}.contents`);
    if (!contents) return;
    const contentTitle = contents[index].title;
    showConfirmModal({
      icon: <ExclamationCircleOutlined style={{ color: 'red' }} />,
      title: 'Excluir Conteúdo',
      content: (
        <Space
          size={'small'}
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
          }}
        >
          <Row>
            <Text>
              {contentTitle
                ? `Deseja realmente excluir o conteúdo "${contentTitle}"? `
                : 'Deseja realmente excluir este conteúdo? '}
            </Text>
          </Row>
          <Row>
            <Text>Essa ação não poderá ser desfeita.</Text>
          </Row>
        </Space>
      ),
      cancelText: 'Cancelar',
      okText: 'Excluir Conteúdo',
      okButtonProps: {
        style: { backgroundColor: 'red', color: 'white', borderColor: 'red' },
      },
      okType: 'danger',
      onOk: () => {
        remove(index);
      },
    });
  };

  const dropdownItems: MenuProps['items'] = [
    { key: 'duplicate', label: 'Duplicar' },
    { key: 'clear', label: 'Limpar' },
    { key: 'remove', label: 'Excluir' },
  ];

  const actionHandlers: Record<string, (index: number) => void> = {
    duplicate: handleDuplicate,
    clear: handleClear,
    remove: handleRemove,
  };

  const handleDropdownAction = useCallback(
    (key: string, index: number) => {
      const action = actionHandlers[key];
      if (action) {
        action(index);
      }
    },
    [actionHandlers]
  );

  useEffect(() => {
    if (contentErrors && contentErrors.length > 0) {
      openAllDropdowns();
    }
  }, [contentErrors, updateDropdownStates]);

  useEffect(() => {
    fields.map((_, index) =>
      updateDropdownStates(prev => {
        if (fields.length - 1 === index) {
          return { ...prev, [index]: true };
        }
        return { ...prev, [index]: false };
      })
    );
  }, [fields.length]);

  return (
    <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <CardList>
        <SortableContext
          items={fields.map((_, contentIdx) => String(contentIdx))}
          strategy={verticalListSortingStrategy}
        >
          {fields.map((field, index) => (
            <SortableItem key={field.id} sortId={String(index)}>
              {(listeners, isDragging) => (
                <ContentCard
                  isDragging={isDragging}
                  listeners={listeners}
                  moduleIndex={moduleIndex}
                  contentIndex={index}
                  isOpen={!!dropdownStates[index]}
                  onToggleDropdown={() => toggleDropdown(index)}
                  addonAfter={
                    <Dropdown
                      menu={{
                        items: dropdownItems,
                        onClick: ({ key }) => handleDropdownAction(key, index),
                      }}
                      trigger={['click']}
                      placement='bottomRight'
                      overlayStyle={{ minWidth: '200px' }}
                    >
                      <TransparentButton
                        icon={<MoreOutlined />}
                        style={{
                          width: '30px',
                          height: '22px',
                        }}
                      />
                    </Dropdown>
                  }
                />
              )}
            </SortableItem>
          ))}
        </SortableContext>

        <Row justify={'center'}>
          <Col>
            <Button
              type='link'
              icon={<PlusCircleOutlined />}
              style={{ marginTop: 16, marginInline: 'auto' }}
              onClick={addEmptyContent}
            >
              Adicionar Conteúdo
            </Button>
          </Col>
        </Row>
      </CardList>
    </DndContext>
  );
});

const CardList = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  padding: 16px;
`;
